//
// Copyright (c) 2026 Comcast
//

import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useRef, useEffect } from 'react'

import colors from 'components/utils/colors'
import Spinner from 'components/Shared/Spinner/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import MarkedText from 'components/Shared/MarkedText/Index'

const DROPDOWN_HEIGHT = 640
const DROPDOWN_WIDE_WIDTH = 360

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: ${props => props.$narrow ? '100%' : `${DROPDOWN_WIDE_WIDTH}px`};
  display: flex;
  flex-direction: column;

  border: 1px solid ${colors.borderSecondaryDefaultDividerList};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.287);
  z-index: 1500;
  max-height: ${DROPDOWN_HEIGHT}px;

  background-color: #fff;
`

const ResultsList = styled.div`
`

const ResultsHeading = styled.div`
  height: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  font-size: 10px;
  font-family: Exo;
  font-weight: 600;
  color: ${colors.contentSecondaryTitle};
  padding: 0px 8px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`

const dropdownItemStyle = css`
  height: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
  padding: 0 8px;
`

const focusedStyle = css`
  background-color: ${colors.primary90};
  outline: none;
`

const TicketLink = styled(Link)`
  ${dropdownItemStyle}
  cursor: pointer;
  gap: 6px;

  &:hover,
  &:focus {
    ${focusedStyle}
  }

  ${props => props.$focused && focusedStyle}

  // divider between adjacent tickets within a group (not before GroupHeading)
  & + & {
    border-top: 1px solid ${colors.borderSecondaryDefaultDividerList};
  }
`

const TicketNumber = styled.div`
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
  color: ${colors.contentSecondaryActive};
  white-space: nowrap;
  flex-shrink: 0;
  width: 100px;
`

const TicketTitle = styled.div`
  font-family: Exo;
  font-size: 12px;
  line-height: 16px;
  color: ${colors.contentSecondaryTitle};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`

const StyledViewAllLink = styled(Link)`
  ${dropdownItemStyle}

  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-top: 1px solid ${colors.secondary80};

  && {
    background-color: #fff;
  }

  &:hover,
  &:focus {
    ${focusedStyle}
  }

  ${props => props.$focused && focusedStyle}
`

const LoadingMessage = styled.div`
  ${dropdownItemStyle}
`

const LoadingOverlay = styled.div`
  ${dropdownItemStyle}
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.85);
  z-index: 1;
`

const StaleResults = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;

  ${props => props.$stale && css`
    opacity: 0.4;
    pointer-events: none;
  `}
`

const NoResultsMessage = styled.div`
  ${dropdownItemStyle}
  font-size: 14px;
  color: ${colors.contentSecondaryActive};
  background-color: ${colors.bgSecondaryStripe};
`

const GroupHeading = styled.div`
  height: 24px;
  min-height: 24px;
  display: flex;
  align-items: center;
  font-size: 10px;
  font-family: Exo;
  font-weight: 600;
  color: ${colors.contentSecondaryTitle};
  padding: 0px 8px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background-color: ${colors.bgSecondaryStripe};
`

const SearchDropdown = ({
  isLoading,
  searchResults,
  viewAllUrl,
  buildTicketUrl,
  onLinkClick,
  focusedIndex,
  searchValue,
}) => {
  const { t } = useTranslation()
  const itemRefs = useRef([])

  const ticketCount = isLoading ? 0 : (searchResults?.groups?.reduce((sum, g) => sum + g.tickets.length, 0) || 0)
  const viewAllIndex = ticketCount

  useEffect(() => {
    if (focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
      itemRefs.current[focusedIndex].focus()
    }
  }, [focusedIndex])

  const viewAllLink = (
    <StyledViewAllLink
      ref={el => { itemRefs.current[viewAllIndex] = el }}
      to={viewAllUrl}
      onClick={onLinkClick}
      $focused={focusedIndex === viewAllIndex}
      tabIndex={-1}
      data-test='global-search-view-all'
    >
      {t('mainHeader.globalSearch.viewAllResults')}
    </StyledViewAllLink>
  )

  const hasResults = searchResults?.groups?.length > 0
  const narrow = !hasResults

  if (isLoading && !hasResults) {
    return (
      <DropdownContainer $narrow>
        <LoadingMessage data-test='global-search-loading'>
          <Spinner
            size={24}
            text={t('common.loading')}
            textSize='12px'
          />
        </LoadingMessage>
        {viewAllLink}
      </DropdownContainer>
    )
  }

  let flatIndex = 0
  const renderGroup = (group) => {
    const heading = (
      <GroupHeading key={`heading-${group.name}`} data-test='global-search-group-heading'>
        {group.name}
      </GroupHeading>
    )

    const tickets = group.tickets.map((ticket) => {
      const index = flatIndex++
      return (
        <TicketLink
          key={ticket.id}
          ref={!isLoading ? el => { itemRefs.current[index] = el } : undefined}
          to={buildTicketUrl(ticket)}
          onClick={onLinkClick}
          $focused={!isLoading && focusedIndex === index}
          tabIndex={-1}
          data-test='global-search-ticket'
          data-tooltip-id='global-search-ticket-tooltip'
          data-tooltip-content={`${ticket.number || ticket.id} ${ticket.title}`}
        >
          <TicketNumber>
            <MarkedText text={ticket.number || ticket.id} markedPart={searchValue} />
          </TicketNumber>
          <TicketTitle>
            <MarkedText text={ticket.title} markedPart={searchValue} />
          </TicketTitle>
        </TicketLink>
      )
    })

    return [heading, ...tickets]
  }

  return (
    <>
      <DropdownContainer $narrow={narrow}>
        {isLoading && (
          <LoadingOverlay data-test='global-search-loading'>
            <Spinner
              size={24}
              text={t('common.loading')}
              textSize='12px'
            />
          </LoadingOverlay>
        )}
        {searchResults?.resultsLabel && (
          <ResultsHeading data-test='global-search-results-heading'>{searchResults.resultsLabel}</ResultsHeading>
        )}
        <StaleResults $stale={isLoading}>
          <ResultsList>
            {searchResults?.groups?.length > 0
              ? searchResults.groups.flatMap(renderGroup)
              : <NoResultsMessage data-test='global-search-no-results'>{t('mainHeader.globalSearch.noResults')}</NoResultsMessage>
            }
          </ResultsList>
        </StaleResults>
        {viewAllLink}
      </DropdownContainer>
      <Tooltip
        id='global-search-ticket-tooltip'
        openEvents={{ mouseenter: true, focus: false }}
        closeEvents={{ mouseleave: true, blur: false }}
      />
    </>
  )
}

SearchDropdown.propTypes = {
  isLoading: PropTypes.bool,
  searchResults: PropTypes.shape({
    groups: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      tickets: PropTypes.array,
    })),
    resultsLabel: PropTypes.string,
    isExactMatch: PropTypes.bool,
  }),
  viewAllUrl: PropTypes.string.isRequired,
  buildTicketUrl: PropTypes.func.isRequired,
  onLinkClick: PropTypes.func,
  focusedIndex: PropTypes.number,
  searchValue: PropTypes.string,
}

export default SearchDropdown
